
import type { ReactNode} from 'react';
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { isAxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { authAPI } from '@/lib/api/auth';
import logger from '@/lib/utils/logger';
import analyticsAPI from '@/lib/api/analytics';
import { detectDeviceType, detectBrowserName } from '@/lib/utils/analytics';

// Anonymous session conversion helper
const ANONYMOUS_ID_KEY = 'ero_anonymous_id';

async function convertAnonymousSession(userId: number): Promise<void> {
  try {
    const anonymousId = localStorage.getItem(ANONYMOUS_ID_KEY);
    if (!anonymousId) return;

    const response = await fetch('/api/v1/analytics/anonymous/convert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        anonymous_id: anonymousId,
        user_id: userId,
      }),
    });

    if (response.ok) {
      localStorage.removeItem(ANONYMOUS_ID_KEY);
    }
  } catch {
    // Silent fail for analytics
  }
}

interface User {
  id: number;
  username: string;
  email: string;
  role: 'user' | 'partner_admin' | 'demo_admin' | 'admin' | 'root_admin';
  is_active: boolean;
  is_verified: boolean;
  is_adult_verified: boolean;
  credits: number;
  membership_type: 'free' | 'basic' | 'premium' | 'vip';
  membership_expires_at?: string | null;
  created_at: string;
  full_name?: string | null;
  permissions?: string[];
  doi_config?: {
    required: boolean;
    delay_minutes: number;
    registration_timestamp: string | null;
  };
}

// Separate interface for state (changes on login/logout)
interface AuthStateContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  loading: boolean; // alias for isLoading
  isAuthenticated: boolean;
}

// Separate interface for actions (never changes)
interface AuthActionsContextType {
  login: (email: string, password: string) => Promise<{ success: boolean; user: User }>;
  register: (username: string, email: string, password: string, referredBy?: string) => Promise<{ success: boolean; user: User }>;
  logout: () => void;
  updateUserBalance: (newBalance: number) => void;
  refreshTokenOnActivity: () => Promise<void>;
}

// Combined interface for backwards compatibility
interface AuthContextType extends AuthStateContextType, AuthActionsContextType {}

// Separate contexts for state and actions
const AuthStateContext = createContext<AuthStateContextType | undefined>(undefined);
const AuthActionsContext = createContext<AuthActionsContextType | undefined>(undefined);

// Hook for state only - use this when you only need user/token/isAuthenticated
export const useAuthState = (): AuthStateContextType => {
  const context = useContext(AuthStateContext);
  if (context === undefined) {
    throw new Error('useAuthState must be used within an AuthProvider');
  }
  return context;
};

// Hook for actions only - use this when you only need login/logout/etc
export const useAuthActions = (): AuthActionsContextType => {
  const context = useContext(AuthActionsContext);
  if (context === undefined) {
    throw new Error('useAuthActions must be used within an AuthProvider');
  }
  return context;
};

// Combined hook for backwards compatibility
export const useAuth = (): AuthContextType => {
  const state = useAuthState();
  const actions = useAuthActions();
  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
};

interface AuthProviderProps {
  children: ReactNode;
}

// Mock user removed - using real API authentication only

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Use ref for activity tracking to avoid re-renders on every mouse move
  const lastActivityRef = useRef<number>(Date.now());
  const lastRefreshRef = useRef<number>(Date.now());
  const refreshingRef = useRef<boolean>(false);
  // Throttle für handleActivity - nur alle 5 Sekunden ausführen
  const lastActivityExecutedRef = useRef<number>(0);

  // Refs for stable callbacks that need access to current state
  const userRef = useRef<User | null>(null);
  const tokenRef = useRef<string | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      try {
        const response = await authAPI.getCurrentUser();
        if (cancelled) {
          return;
        }

        setUser(response.data);
        // Cookie-basierte Auth – Marker genügt
        setToken('cookie');
        lastRefreshRef.current = Date.now();
        logger.debug('Auth initialized for user:', response.data.username);

        // Invalidate design-configuration to ensure feature flags are fresh
        void queryClient.invalidateQueries({ queryKey: ['design-configuration'] });

        // Ensure analytics session exists also on auto-login/init
        try {
          const key = '__analytics_session_id__';
          let sid = sessionStorage.getItem(key);
          if (!sid) {
            sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            sessionStorage.setItem(key, sid);
          }
          // Create only once per tab
          const createdFlag = '__analytics_session_created__';
          if (!sessionStorage.getItem(createdFlag)) {
            await analyticsAPI.sessions.create({
              session_id: sid,
              device_type: detectDeviceType(navigator.userAgent),
              browser: detectBrowserName(navigator.userAgent),
              referrer: document.referrer ?? '',
            });
            sessionStorage.setItem(createdFlag, '1');
          }
        } catch {
          // ignore analytics errors on init
        }
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }

        // 401 vor Login: nur auf debug, nicht als warn/error
        if (isAxiosError(error) && error.response?.status === 401) {
          logger.debug('Auth initialization: unauthenticated (401)');
        } else {
          logger.warn('Auth initialization failed:', error);
        }
        setToken(null);
        setUser(null);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    initAuth().catch((error: unknown) => {
      if (!cancelled) {
        logger.error('Critical auth initialization error:', error);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Stable login callback - never changes reference
  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });
      const { user: userData } = response.data;
      // Update state immediately
      setToken('cookie');
      setUser(userData);
      lastRefreshRef.current = Date.now();

      // Force a state update to ensure components re-render
      logger.info('Login successful:', { user: userData.username, role: userData.role });

      // Create analytics session for this user
      try {
        const key = '__analytics_session_id__';
        let sid = sessionStorage.getItem(key);
        if (!sid) {
          sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
          sessionStorage.setItem(key, sid);
        }
        await analyticsAPI.sessions.create({
          session_id: sid,
          device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          browser: detectBrowserName(navigator.userAgent),
          referrer: document.referrer ?? '',
        });
      } catch {
        // ignore analytics errors
      }

      // Convert anonymous session to authenticated user (link pre-auth events)
      void convertAnonymousSession(userData.id);

      // Invalidate design-configuration to reload feature flags (e.g., voice memo)
      void queryClient.invalidateQueries({ queryKey: ['design-configuration'] });

      return { success: true, user: userData };
    } catch (error: unknown) {
      logger.error('Login error:', error);
      // Clear any partial state
      setToken(null);
      setUser(null);
      // Cookies werden serverseitig verwaltet
      throw error;
    }
  }, [queryClient]);

  // Stable register callback - never changes reference
  const register = useCallback(async (username: string, email: string, password: string, referredBy?: string) => {
    const params = referredBy ? { referred_by: referredBy } : undefined;
    const response = await authAPI.register({ username, email, password }, params);
    const { user: userData } = response.data;
    setToken('cookie');
    setUser(userData);

    // Convert anonymous session to authenticated user (link pre-auth events)
    void convertAnonymousSession(userData.id);

    // Invalidate design-configuration to reload feature flags
    void queryClient.invalidateQueries({ queryKey: ['design-configuration'] });

    return { success: true, user: userData };
  }, [queryClient]);

  // Stable logout callback - never changes reference
  const logout = useCallback(() => {
    // Serverseitigen Logout ausführen, Cookies werden dort gelöscht
    try {
      void authAPI.logout();
    } catch {
      // Ignorieren – selbst bei Fehlern den lokalen Zustand leeren
    }
    setToken(null);
    setUser(null);
    // End analytics session if exists
    try {
      const sid = sessionStorage.getItem('__analytics_session_id__');
      if (sid) {
        void analyticsAPI.sessions.end(sid);
      }
    } catch {
      // ignore
    }
  }, []);

  // Cookie-basierte Auth: Eingeloggt, sobald ein User geladen ist
  const isAuthenticated = !!user;

  // Stable updateUserBalance callback - uses ref to avoid dependency on user state
  const updateUserBalance = useCallback((newBalance: number) => {
    setUser(prevUser => {
      if (prevUser) {
        return {
          ...prevUser,
          credits: newBalance
        };
      }
      return prevUser;
    });
  }, []);

  // Stable refreshTokenOnActivity callback - never changes reference
  const refreshTokenOnActivity = useCallback(async () => {
    // Prevent multiple simultaneous refresh attempts
    if (refreshingRef.current) {
      lastActivityRef.current = Date.now();
      return;
    }

    // Cookie-basierter Refresh – nur bei Aktivität auslösen

    // Nur refreshen wenn Token bald abläuft (< 10 Minuten)
    const timeSinceActivity = Date.now() - lastActivityRef.current;
    const tenMinutes = 10 * 60 * 1000;

    if (timeSinceActivity < tenMinutes) {
      // Token ist noch frisch, nur Activity updaten
      lastActivityRef.current = Date.now();
      return;
    }

    refreshingRef.current = true;
    try {
      await authAPI.refresh();
      setToken('cookie');
      lastActivityRef.current = Date.now();
      lastRefreshRef.current = Date.now();

      logger.debug('Token refreshed on activity');
    } catch (error: unknown) {
      logger.warn('Token refresh failed:', error);
      logout();
    } finally {
      refreshingRef.current = false;
    }
  }, [logout]);

  // Activity Tracking – erweitert um Fokus/Visibility und Route-Wechsel
  useEffect(() => {
    if (!token) return;

    const handleActivity = () => {
      const now = Date.now();

      // Throttle: nur alle 5 Sekunden wirklich ausführen
      const THROTTLE_MS = 5000;
      if (now - lastActivityExecutedRef.current < THROTTLE_MS) {
        return;
      }
      lastActivityExecutedRef.current = now;

      // Update ref instead of state to avoid re-renders
      lastActivityRef.current = now;

      // Proactive token refresh when enough time has passed since the last
      // refresh. Triggered by real user activity rather than a fixed timer —
      // an idle tab will let the session expire naturally, and the next
      // interaction (or a 401 from any API call) picks things up.
      const REFRESH_INTERVAL_MS = 20 * 60 * 1000;
      if (!refreshingRef.current && now - lastRefreshRef.current > REFRESH_INTERVAL_MS) {
        refreshingRef.current = true;
        authAPI.refresh()
          .then(() => {
            lastRefreshRef.current = Date.now();
            setToken('cookie');
            logger.debug('Token auto-refreshed on activity');
          })
          .catch((error: unknown) => {
            logger.warn('Activity-triggered token refresh failed:', error);
            logout();
          })
          .finally(() => {
            refreshingRef.current = false;
          });
      }

      // Update session heartbeat with throttle and error handling
      try {
        const sid = sessionStorage.getItem('__analytics_session_id__');
        if (!sid) return;
        const lastKey = '__analytics_last_update__';
        const now = Date.now();
        const last = Number(sessionStorage.getItem(lastKey) || 0);
        const minIntervalMs = 30000; // 30s throttle
        if (now - last < minIntervalMs) return;
        sessionStorage.setItem(lastKey, String(now));
        const createdFlag = '__analytics_session_created__';
        // If session was never created (or creation failed earlier), try create once
        if (!sessionStorage.getItem(createdFlag)) {
          analyticsAPI.sessions.create({
            session_id: sid,
            device_type: detectDeviceType(navigator.userAgent),
            browser: detectBrowserName(navigator.userAgent),
            referrer: document.referrer ?? '',
          })
          .then(() => {
            sessionStorage.setItem(createdFlag, '1');
          })
          .catch((error: unknown) => {
            logger.debug('Analytics session create failed', error);
          });
          return;
        }
        analyticsAPI.sessions.update(sid, { is_active: true }).catch((err: unknown) => {
          // If update says 404, try to create once and then ignore errors
          if (isAxiosError(err) && err.response?.status === 404) {
            analyticsAPI.sessions.create({
              session_id: sid,
              device_type: detectDeviceType(navigator.userAgent),
              browser: detectBrowserName(navigator.userAgent),
              referrer: document.referrer ?? '',
            }).then(() => {
              sessionStorage.setItem(createdFlag, '1');
            }).catch((error: unknown) => {
              logger.debug('Analytics session recreate failed', error);
            });
          }
        });
      } catch {
        // ignore
      }
    };

    // Track verschiedene Activity-Events
    const events = ['click', 'keydown', 'scroll', 'mousemove', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Sichtbarkeit/Fokus des Tabs
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        handleActivity();
      }
    };
    const handleFocus = () => handleActivity();
    const handlePageShow = () => handleActivity();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);

    // Route-Wechsel via History API abfangen
    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);
    const handleHistoryChange = () => handleActivity();
    history.pushState = (
      ...args: Parameters<typeof history.pushState>
    ) => {
      const ret = origPush(...args);
      handleHistoryChange();
      return ret;
    };
    history.replaceState = (
      ...args: Parameters<typeof history.replaceState>
    ) => {
      const ret = origReplace(...args);
      handleHistoryChange();
      return ret;
    };
    window.addEventListener('popstate', handleHistoryChange);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      history.pushState = origPush;
      history.replaceState = origReplace;
      window.removeEventListener('popstate', handleHistoryChange);
    };
  }, [token]);

  // Session-Ende signalisieren (pagehide/beforeunload)
  useEffect(() => {
    if (!token) return;
    const handler = () => {
      try {
        const sid = sessionStorage.getItem('__analytics_session_id__');
        if (sid) {
          // Fire-and-forget; Browser kann Requests bei unload abbrechen, aber sendBeacon wäre CORS-sensitiv
          void analyticsAPI.sessions.end(sid);
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener('pagehide', handler);
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('pagehide', handler);
      window.removeEventListener('beforeunload', handler);
    };
  }, [token]);

  // Memoize state value - only changes when state actually changes
  const stateValue = useMemo<AuthStateContextType>(() => ({
    user,
    token,
    isLoading,
    loading: isLoading,
    isAuthenticated,
  }), [user, token, isLoading, isAuthenticated]);

  // Memoize actions value - these functions never change (stable references via useCallback)
  const actionsValue = useMemo<AuthActionsContextType>(() => ({
    login,
    register,
    logout,
    updateUserBalance,
    refreshTokenOnActivity,
  }), [login, register, logout, updateUserBalance, refreshTokenOnActivity]);

  return (
    <AuthStateContext.Provider value={stateValue}>
      <AuthActionsContext.Provider value={actionsValue}>
        {children}
      </AuthActionsContext.Provider>
    </AuthStateContext.Provider>
  );
};
